import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Bug, ShieldCheck, X } from 'lucide-react';
import toast from 'react-hot-toast';

const linkStyle: React.CSSProperties = {
  display: 'block',
  margin: 0,
  padding: 0,
  lineHeight: 1.2,
  background: 'none',
  border: 'none',
  height: 'auto',
  minHeight: 0,
  boxSizing: 'border-box',
};

const openBugReport = () => {
  const dlg = document.getElementById('bugReportModal') as HTMLDialogElement | null;
  if (!dlg) return;

  const pageInput = dlg.querySelector('#bugPage') as HTMLInputElement | null;
  if (pageInput && !pageInput.value) pageInput.value = window.location.href;
  dlg.showModal();
};

const closeBugReport = () => {
  (document.getElementById('bugReportModal') as HTMLDialogElement | null)?.close();
};

const Footer: React.FC = () => {
  const { t } = useTranslation();

  return (
    <>
      <footer className="footer">
        <div className="footer-container">
          <div className="footer-column">
            <h4>{t('footerStart')}</h4>
            <div className="footer-links">
              <Link to="/concept-validator" style={linkStyle}>{t('footerValidateIdea')}</Link>
              <Link to="/pricing" style={linkStyle}>{t('navbarPricing')}</Link>
              <Link to="/programs" style={linkStyle}>{t('footerPrograms')}</Link>
            </div>
          </div>

          <div className="footer-column">
            <h4>{t('footerSupport')}</h4>
            <div className="footer-links">
              <Link to="/help-center" style={linkStyle}>{t('footerHelpCenter')}</Link>
              <a href="mailto:info@XPROJEX.com" style={linkStyle}>{t('footerContact')}</a>
              <button
                type="button"
                onClick={openBugReport}
                className="footer-text-button"
                aria-label={t('footerReportIssue')}
              >
                <Bug size={14} />
                <span>{t('footerReportIssue')}</span>
              </button>
            </div>
          </div>

          <div className="footer-column">
            <h4>{t('footerLegal')}</h4>
            <div className="footer-links">
              <a href="https://www.XPROJEX.com/terms-of-use" target="_blank" rel="noopener noreferrer" style={linkStyle}>{t('footerTerms')}</a>
              <a href="https://www.XPROJEX.com/privacy-policy" target="_blank" rel="noopener noreferrer" style={linkStyle}>{t('footerPrivacy')}</a>
              <a href="https://www.XPROJEX.com/privacy-policy" target="_blank" rel="noopener noreferrer" style={linkStyle}>{t('footerCookies')}</a>
            </div>
          </div>
        </div>

        <div className="footer-bottom">
          <p>{t('footerCopy')}</p>
          <div className="footer-responsible-ai">
            <ShieldCheck size={13} color="var(--success-text)" style={{ flexShrink: 0 }} />
            <span className="footer-responsible-ai-label">{t('footerResponsibleAI')}</span>
            <span className="footer-responsible-ai-desc">{t('footerResponsibleAIDesc')}</span>
          </div>
        </div>
      </footer>

      <dialog id="bugReportModal" className="bug-report-dialog">
        <form
          method="dialog"
          className="bug-report-form"
          onSubmit={async (e) => {
            e.preventDefault();

            const form = e.currentTarget as HTMLFormElement;
            const email = (form.querySelector('#bugEmail') as HTMLInputElement).value.trim();
            const page = (form.querySelector('#bugPage') as HTMLInputElement).value.trim();
            const category = (form.querySelector('#bugCategory') as HTMLSelectElement).value;
            const severity = (form.querySelector('#bugSeverity') as HTMLSelectElement).value;
            const steps = (form.querySelector('#bugSteps') as HTMLTextAreaElement).value.trim();
            const expected = (form.querySelector('#bugExpected') as HTMLTextAreaElement).value.trim();
            const actual = (form.querySelector('#bugActual') as HTMLTextAreaElement).value.trim();
            const includeMeta = (form.querySelector('#bugIncludeMeta') as HTMLInputElement).checked;

            if (!page || steps.length < 10 || actual.length < 5) {
              toast.error(t('bugReportValidation'));
              return;
            }

            const subject = `Bug (${category} / ${severity}) - ${page}`;
            const fullBody =
              `Reporter: ${email}\n` +
              `Page/URL: ${page}\nCategory: ${category}\nSeverity: ${severity}\n\n` +
              `Steps to Reproduce:\n${steps}\n\n` +
              `Expected:\n${expected}\n\n` +
              `Actual:\n${actual}` +
              (includeMeta ? `\n\n---\nUA: ${navigator.userAgent}\nViewport: ${window.innerWidth}x${window.innerHeight}\nTime: ${new Date().toISOString()}` : '');

            const maxMailto = 1800;
            let bodyForMailto = fullBody;

            if (fullBody.length > maxMailto) {
              try {
                await navigator.clipboard.writeText(fullBody);
              } catch {
                // Clipboard access is best effort only.
              }
              bodyForMailto = `${t('bugReportCopied')}\n\n${t('bugReportPasteHint')}\n\n${fullBody.slice(0, 800)}...`;
            }

            window.location.href =
              `mailto:info@XPROJEX.com` +
              `?cc=${encodeURIComponent(email)}` +
              `&subject=${encodeURIComponent(subject)}` +
              `&body=${encodeURIComponent(bodyForMailto)}`;

            setTimeout(closeBugReport, 250);
          }}
        >
          <div className="bug-report-header">
            <div>
              <h3>
                <Bug size={16} />
                {t('bugReportTitle')}
              </h3>
              <p>{t('bugReportSubtitle')}</p>
            </div>
            <button type="button" onClick={closeBugReport} className="bug-report-close" aria-label={t('commonClose')}>
              <X size={18} />
            </button>
          </div>

          <div className="bug-report-body">
            <div className="bug-report-grid">
              <label>
                {t('bugEmailLabel')}
                <input id="bugEmail" type="email" required placeholder={t('autogen.50e2b46e')} />
              </label>

              <label>
                {t('bugPageLabel')}
                <input id="bugPage" type="text" placeholder="https://..." defaultValue={typeof window !== 'undefined' ? window.location.href : ''} required />
              </label>

              <label>
                {t('bugCategoryLabel')}
                <select id="bugCategory" defaultValue={t('bugCategoryUX')}>
                  <option>{t('bugCategoryUX')}</option>
                  <option>{t('bugCategoryData')}</option>
                  <option>{t('bugCategoryPerformance')}</option>
                  <option>{t('bugCategoryPayments')}</option>
                  <option>{t('bugCategoryOther')}</option>
                </select>
              </label>

              <label>
                {t('bugSeverityLabel')}
                <select id="bugSeverity" defaultValue={t('bugSeverityMedium')}>
                  <option>{t('bugSeverityLow')}</option>
                  <option>{t('bugSeverityMedium')}</option>
                  <option>{t('bugSeverityHigh')}</option>
                  <option>{t('bugSeverityCritical')}</option>
                </select>
              </label>
            </div>

            <label>
              {t('bugStepsLabel')}
              <textarea id="bugSteps" rows={4} required minLength={10} placeholder={t('bugStepsPlaceholder')} />
            </label>

            <div className="bug-report-grid">
              <label>
                {t('bugExpectedLabel')}
                <textarea id="bugExpected" rows={3} required placeholder={t('bugExpectedPlaceholder')} />
              </label>

              <label>
                {t('bugActualLabel')}
                <textarea id="bugActual" rows={3} required placeholder={t('bugActualPlaceholder')} />
              </label>
            </div>

            <label className="bug-report-checkbox">
              <input id="bugIncludeMeta" type="checkbox" defaultChecked />
              <span>{t('bugIncludeMetaLabel')}</span>
            </label>
          </div>

          <div className="bug-report-actions">
            <button type="button" className="btn-secondary" onClick={closeBugReport}>
              {t('bugCancelButton')}
            </button>
            <button type="submit" className="btn-primary">
              {t('bugSendButton')}
            </button>
          </div>
        </form>
      </dialog>
    </>
  );
};

export default Footer;
